'use client';

import { useEffect, useState } from 'react';
import { type ClassValue, clsx } from 'clsx'
import { twMerge } from 'tailwind-merge'

// Inline cn function to avoid circular dependency issues
function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

interface SkeletonProps extends React.HTMLAttributes<HTMLDivElement> {
  timeout?: number; // Auto-hide after timeout (in milliseconds)
}

function Skeleton({
  className,
  timeout = 2000, // 2 seconds max loading time
  ...props
}: SkeletonProps) {
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    if (timeout > 0) {
      const timer = setTimeout(() => {
        setVisible(false);
      }, timeout);

      return () => clearTimeout(timer);
    }
  }, [timeout]);

  if (!visible) {
    return null;
  }

  return (
    <div
      className={cn("shimmer animate-pulse rounded-md bg-gray-200", className)}
      {...props}
    />
  )
}

interface SkeletonCardProps {
  className?: string;
  timeout?: number;
}

function SkeletonCard({ className, timeout = 2000 }: SkeletonCardProps) {
  return (
    <div className={cn("rounded-lg border p-6 shadow-soft", className)}>
      <div className="space-y-4">
        <Skeleton timeout={timeout} className="h-48 w-full rounded-lg" />
        <div className="space-y-2">
          <Skeleton timeout={timeout} className="h-4 w-3/4" />
          <Skeleton timeout={timeout} className="h-4 w-1/2" />
        </div>
        <div className="flex space-x-2">
          <Skeleton timeout={timeout} className="h-6 w-16 rounded-full" />
          <Skeleton timeout={timeout} className="h-6 w-20 rounded-full" />
        </div>
      </div>
    </div>
  )
}

interface SkeletonMakerCardProps {
  className?: string;
}

function SkeletonMakerCard({ className }: SkeletonMakerCardProps) {
  return (
    <div className={cn("w-[300px] flex-shrink-0 rounded-lg overflow-hidden shadow-soft", className)}>
      <div className="relative">
        <Skeleton className="h-[366px] w-full" />
        <div className="absolute top-4 right-4">
          <Skeleton className="w-[91px] h-[91px] rounded-full" />
        </div>
        <div className="absolute bottom-4 left-4 right-4 space-y-2">
          <Skeleton className="h-3 w-32 mx-auto" />
          <Skeleton className="h-8 w-48 mx-auto" />
        </div>
      </div>
      <div className="p-4 space-y-4">
        <div className="space-y-2">
          <Skeleton className="h-3 w-full" />
          <Skeleton className="h-3 w-4/5" />
          <Skeleton className="h-3 w-3/5" />
        </div>
        <div className="flex justify-between items-center">
          <div className="flex space-x-2">
            <Skeleton className="h-6 w-16 rounded-full" />
            <Skeleton className="h-6 w-20 rounded-full" />
          </div>
          <Skeleton className="h-3 w-16" />
        </div>
      </div>
    </div>
  )
}

interface SkeletonGridProps {
  count?: number;
  className?: string;
}

function SkeletonGrid({ count = 3, className }: SkeletonGridProps) {
  return (
    <div className={cn("flex gap-8 overflow-hidden", className)}>
      {Array.from({ length: count }, (_, i) => (
        <SkeletonMakerCard key={i} />
      ))}
    </div>
  )
}

export { Skeleton, SkeletonCard, SkeletonMakerCard, SkeletonGrid }